// Copyright 2025, University of Maryland, All Rights Reserved
import React from 'react';
import {ChartRenderer} from "@/components/renderers/ChartRenderer.tsx";

interface DataTypeRendererProps {
  data: any;
  level?: number;
}

/**
 * Renders different data types appropriately.
 * Handles strings, numbers, arrays, objects, booleans.
 * NO FALLBACKS - Only renders what actually exists.
 */
const DataTypeRenderer: React.FC<DataTypeRendererProps> = ({ data, level = 0 }) => {
  if (data === null || data === undefined) {
    return <span className="text-gray-400 italic">Not provided</span>;
  }

  // Handle different data types
  if (typeof data === 'string') {
    return <StringRenderer value={data} />;
  }

  if (typeof data === 'number') {
    return <NumberRenderer value={data} />;
  }

  if (typeof data === 'boolean') {
    return <BooleanRenderer value={data} />;
  }

  if (Array.isArray(data)) {
    return <ArrayRenderer data={data} level={level} />;
  }

  if (typeof data === 'object') {

    // Check if this is chart data (single chart object)
    if (data.chart_type) {
      return <ChartRenderer chartData={data} />;
    }

    // Check if this is a visualizations container with multiple charts
    // Dynamically detect any properties that are chart objects
    const chartEntries = Object.entries(data).filter(([_, value]) => 
      value && 
      typeof value === 'object' && 
      !Array.isArray(value) && 
      (value as any).chart_type
    );

    if (chartEntries.length > 0) {
      // Find difficulty_distribution and topic_coverage_comparison for special layout
      const difficultyChart = chartEntries.find(([name]) => name === 'difficulty_distribution');
      const heatmapChart = chartEntries.find(([name]) => name === 'topic_coverage_comparison');
      const otherCharts = chartEntries.filter(([name]) => 
        name !== 'difficulty_distribution' && name !== 'topic_coverage_comparison'
      );

      return (
        <div className="space-y-4">
          {/* Difficulty Distribution and Heatmap - single column */}
          {(difficultyChart || heatmapChart) && (
            <div className="grid grid-cols-1 gap-4">
              {difficultyChart && (
                <div>
                  <ChartRenderer chartData={difficultyChart[1]} />
                </div>
              )}
              {heatmapChart && (
                <div>
                  <ChartRenderer chartData={heatmapChart[1]} />
                </div>
              )}
            </div>
          )}

          {/* Other charts - single column */}
          {otherCharts.length > 0 && (
            <div className="grid grid-cols-1 gap-4">
              {otherCharts.map(([chartName, chartData], index) => (
                <div key={`${chartName}-${index}`}>
                  <ChartRenderer chartData={chartData} />
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return <ObjectRenderer data={data} level={level} />;
  }

  // Fallback to string representation for unknown types
  return <span className="text-gray-600 font-mono text-sm">{String(data)}</span>;
};

// String renderer with formatting
const StringRenderer: React.FC<{ value: string }> = ({ value }) => {
  // Check if it looks like a percentage
  // if (value.includes('%')) {
  //   return <span className="text-2xl font-bold text-blue-600">{value}</span>;
  // }

  // Check if it's a rating (Poor, Fair, Good, Excellent)
  const ratings = ['poor', 'fair', 'good', 'excellent'];
  if (ratings.includes(value.toLowerCase())) {
    const getRatingColor = (rating: string) => {
      switch (rating.toLowerCase()) {
        case 'excellent': return 'text-green-800 bg-green-100';
        case 'good': return 'text-blue-800 bg-blue-100';
        case 'fair': return 'text-yellow-800 bg-yellow-100';
        case 'poor': return 'text-red-800 bg-red-100';
        default: return 'text-gray-800 bg-gray-100';
      }
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRatingColor(value)}`}>
        {value}
      </span>
    );
  }

  // Regular text
  return <p className="text-gray-700 leading-relaxed">{value}</p>;
};

// Number renderer with formatting
const NumberRenderer: React.FC<{ value: number }> = ({ value }) => {
  // Check if it's a percentage (0-1 range)
  if (value >= 0 && value <= 1) {
    return (
      <div className="flex items-center space-x-2">
        <span className="text-2xl font-bold text-blue-600">{(value * 100).toFixed(1)}%</span>
        <div className="w-32 bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${value * 100}%` }}
          ></div>
        </div>
      </div>
    );
  }

  // Check if it's a score (1-10 range)
  if (value >= 1 && value <= 10 && Number.isInteger(value)) {
    return (
      <div className="flex items-center space-x-2">
        <span className="text-2xl font-bold text-purple-600">{value}/10</span>
        <div className="w-32 bg-gray-200 rounded-full h-2">
          <div 
            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(value / 10) * 100}%` }}
          ></div>
        </div>
      </div>
    );
  }

  // Regular number
  return <span className="text-lg font-semibold text-gray-800">{value}</span>;
};

// Boolean renderer
const BooleanRenderer: React.FC<{ value: boolean }> = ({ value }) => {
  return (
    <span className={`px-2 py-1 rounded text-sm font-medium ${
      value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`}>
      {value ? '✅ Yes' : '❌ No'}
    </span>
  );
};

// Array renderer
const ArrayRenderer: React.FC<{ data: any[]; level: number }> = ({ data, level }) => {
  if (data.length === 0) {
    return <span className="text-gray-400 italic">No items</span>;
  }

  // Check if all items are strings (render as list)
  if (data.every(item => typeof item === 'string')) {
    return (
      <ul className="space-y-2">
        {data.map((item, index) => (
          <li key={index} className="flex items-start">
            <span className="text-blue-800 mr-2 mt-1">•</span>
            <span className="text-gray-700">{item}</span>
          </li>
        ))}
      </ul>
    );
  }

  // Check if all items are numbers (could be chart data)
  if (data.every(item => typeof item === 'number')) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-600 mb-2">Data values:</div>
        <div className="flex flex-wrap gap-2">
          {data.map((item, index) => (
            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
              {item}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // Render complex objects in array
  return (
    <div className="space-y-2">
      {data.map((item, index) => (
        <div key={index} className="border-l-2 border-gray-300 pl-4">
          <div className="text-sm text-gray-500 mb-1">Item {index + 1}:</div>
          <DataTypeRenderer data={item} level={level + 1} />
        </div>
      ))}
    </div>
  );
};

// Object renderer
const ObjectRenderer: React.FC<{ data: Record<string, any>; level: number }> = ({ data, level }) => {
  const entries = Object.entries(data);
  
  if (entries.length === 0) {
    return <span className="text-gray-400 italic">No data</span>;
  }

  return (
    <div className="space-y-2">
      {entries.map(([key, value], index) => (
        <div key={index} className={level === 0 ? "border-b border-gray-200 pb-2 last:border-b-0" : ""}>
          <div className="font-medium text-gray-800 mb-2">
            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
          </div>
          <div className={level > 0 ? "ml-4" : ""}>
            <DataTypeRenderer data={value} level={level + 1} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default DataTypeRenderer;